export const modifierCategories=[
  {key: 'all',label: 'All Modifiers'},
  {key: 'em',label: 'E/M Modifiers'},
  {key: 'surgical',label: 'Surgical Modifiers'},
  {key: 'anatomical',label: 'Anatomical Modifiers'},
  {key: 'diagnostic',label: 'Diagnostic / Technical'},
  {key: 'distinct',label: 'Surgical / Distinct'},
  {key: 'general',label: 'General'},
  {key: 'payment',label: 'HCPCS / Payment'},
];

export const modifiers=[
  {
    code: '-25',
    name: 'Significant, separately identifiable E/M service',
    category: 'em',
    categoryLabel: 'E/M',
    rule: 'Append to the E/M code when a significant, separately identifiable service is performed on the same day as another procedure or service.',
    tip: 'Document the separate history, exam, medical decision making, or other qualifying work.',
  },
  {
    code: '-24',
    name: 'Unrelated E/M service during postoperative period',
    category: 'em',
    categoryLabel: 'E/M',
    rule: 'Identifies an unrelated E/M service provided by the same physician during a postoperative global period.',
    tip: 'The diagnosis and documentation should clearly support that the visit is unrelated to the prior procedure.',
  },
  {
    code: '-57',
    name: 'Decision for surgery',
    category: 'em',
    categoryLabel: 'E/M',
    rule: 'Identifies an E/M service that resulted in the initial decision to perform a major surgery.',
    tip: 'Use for the decision for major surgery, not routinely for minor procedures with zero or ten-day global periods.',
  },
  {
    code: '-50',
    name: 'Bilateral procedure',
    category: 'surgical',
    categoryLabel: 'Surgical',
    rule: 'Reports the same procedure performed bilaterally during one operative session when the code does not already describe a bilateral service.',
    tip: 'Check payer instructions because some payers require two line items with RT and LT instead.',
  },
  {
    code: '-51',
    name: 'Multiple procedures',
    category: 'surgical',
    categoryLabel: 'Surgical',
    rule: 'Identifies multiple procedures performed during the same operative session when appropriate under CPT guidelines.',
    tip: 'Do not append when the code is exempt from modifier 51 or when an add-on code applies.',
  },
  {
    code: '-58',
    name: 'Staged or related procedure during postoperative period',
    category: 'surgical',
    categoryLabel: 'Surgical',
    rule: 'Identifies a staged, planned, or more extensive related procedure during the postoperative period.',
    tip: 'The original operative plan or the clinical documentation should support the staged or related nature of the service.',
  },
  {
    code: '-59',
    name: 'Distinct procedural service',
    category: 'surgical',
    categoryLabel: 'Surgical',
    rule: 'Identifies a procedure or service that is distinct or independent from another service performed on the same day.',
    tip: 'Use a more specific X{EPSU} modifier when available. Do not use -59 solely to bypass an edit.',
  },
  {
    code: '-78',
    name: 'Unplanned return to operating or procedure room',
    category: 'surgical',
    categoryLabel: 'Surgical',
    rule: 'Identifies an unplanned return to the operating or procedure room by the same physician for a related procedure during the postoperative period.',
    tip: 'The return must be unplanned and related to the original procedure.',
  },
  {
    code: '-79',
    name: 'Unrelated procedure during postoperative period',
    category: 'surgical',
    categoryLabel: 'Surgical',
    rule: 'Identifies an unrelated procedure performed by the same physician during the postoperative period.',
    tip: 'Use an unrelated diagnosis and documentation that clearly separates the new procedure from the original service.',
  },
  {
    code: '-22',
    name: 'Increased Procedural Service',
    category: 'surgical',
    categoryLabel: 'Surgical',
    rule: 'Identifies a service that required significantly greater effort, time, or complexity than usual.',
    tip: 'Requires operative report documentation and letter-heavy justification; often triggers manual review.',
  },
  {
    code: '-52',
    name: 'Reduced Services',
    category: 'general',
    categoryLabel: 'General',
    rule: 'Indicates a procedure was partially reduced or terminated at the physician’s discretion.',
    tip: 'Do not use if a procedure is cut short due to sudden clinical risk; use -53 instead.',
  },
  {
    code: '-53',
    name: 'Discontinued Procedure',
    category: 'surgical',
    categoryLabel: 'Surgical',
    rule: 'Procedure was terminated after surgical prep or anesthesia induction due to risk to patient safety.',
    tip: 'Restricted strictly to physician services when anesthesia or surgical intervention has already started.',
  },
  {
    code: '-76',
    name: 'Repeat Procedure by Same Physician',
    category: 'general',
    categoryLabel: 'General',
    rule: 'Indicates a procedure was repeated by the same physician on the same day.',
    tip: 'Essential for repeat X-rays or therapeutic injections performed later in the day.',
  },
  {
    code: '-26',
    name: 'Professional Component',
    category: 'diagnostic',
    categoryLabel: 'Diagnostic / Technical',
    rule: 'Reports only the professional interpretation and report component of a diagnostic test.',
    tip: 'Commonly used when a provider reads an imaging scan or test performed at an outside facility.',
  },
  {
    code: '-TC',
    name: 'Technical Component',
    category: 'diagnostic',
    categoryLabel: 'Diagnostic / Technical',
    rule: 'Reports only the equipment, supplies, and technician overhead of a procedure.',
    tip: 'Separates the facility/equipment charge from the physician’s professional read.',
  },
  {
    code: '-XE',
    name: 'Separate Encounter',
    category: 'distinct',
    categoryLabel: 'Surgical / Distinct',
    rule: 'Service is distinct because it occurred during a separate encounter on the same day.',
    tip: 'Preferred by Medicare over modifier 59 when services happen at different times of day.',
  },
  {
    code: '-XP',
    name: 'Separate Practitioner',
    category: 'distinct',
    categoryLabel: 'Surgical / Distinct',
    rule: 'Service is distinct because a different practitioner performed it.',
    tip: 'Used when multiple providers are independently performing separate services.',
  },
  {
    code: '-XS',
    name: 'Separate Structure',
    category: 'distinct',
    categoryLabel: 'Surgical / Distinct',
    rule: 'Service is distinct because it was performed on a separate organ or anatomical structure.',
    tip: 'Look for different lesion sites or separate anatomical organs in the operative report.',
  },
  {
    code: '-XU',
    name: 'Unusual Non-Overlapping Service',
    category: 'distinct',
    categoryLabel: 'Surgical / Distinct',
    rule: 'Service is distinct because it does not overlap usual component definitions.',
    tip: 'Used for rare exceptions where NCCI edits incorrectly bind independent services.',
  },
  {
    code: '-JW',
    name: 'Unused Drug From Single-Dose Vial Wasted',
    category: 'payment',
    categoryLabel: 'HCPCS / Payment',
    rule: 'Reports the exact amount of drug discarded from a single-use vial.',
    tip: 'Required by Medicare for tracking wasted single-dose specialty medications and biologics.',
  },
  {
    code: '-JZ',
    name: 'Zero Drug Waste From Single-Dose Vial',
    category: 'payment',
    categoryLabel: 'HCPCS / Payment',
    rule: 'Attests that 100% of a single-dose vial was administered with zero waste.',
    tip: 'Mandatory billing attestation whenever a single-use vial is fully utilized.',
  },
  {
    code: '-RT',
    name: 'Right side',
    category: 'anatomical',
    categoryLabel: 'Anatomical',
    rule: 'Identifies a service performed on the right side of the body.',
    tip: 'Use only when the code set and payer instructions support anatomical modifiers.',
  },
  {
    code: '-LT',
    name: 'Left side',
    category: 'anatomical',
    categoryLabel: 'Anatomical',
    rule: 'Identifies a service performed on the left side of the body.',
    tip: 'Verify that the clinical documentation supports the left-sided service.',
  },
  {
    code: '-E1',
    name: 'Upper left eyelid',
    category: 'anatomical',
    categoryLabel: 'Anatomical',
    rule: 'Identifies a service performed on the upper left eyelid.',
    tip: 'Eyelid modifiers require precise documentation of the anatomical site.',
  },
  {
    code: '-E2',
    name: 'Lower left eyelid',
    category: 'anatomical',
    categoryLabel: 'Anatomical',
    rule: 'Identifies a service performed on the lower left eyelid.',
    tip: 'Do not infer the eyelid location when it is not documented.',
  },
  {
    code: '-FA',
    name: 'Left hand, thumb',
    category: 'anatomical',
    categoryLabel: 'Anatomical',
    rule: 'Identifies a service performed on the left hand thumb.',
    tip: 'Review the full finger and hand modifier family before selecting the code.',
  },
  {
    code: '-F9',
    name: 'Right hand, fifth digit',
    category: 'anatomical',
    categoryLabel: 'Anatomical',
    rule: 'Identifies a service performed on the right hand fifth digit.',
    tip: 'Use the documented digit and side rather than selecting a general laterality modifier.',
  },
  {
    code: '-GA',
    name: 'Waiver of liability statement on file',
    category: 'payment',
    categoryLabel: 'HCPCS / Payment',
    rule: 'Indicates that a required waiver of liability statement is on file as specified by payer policy.',
    tip: 'A signed waiver must actually be present; this modifier does not replace medical necessity documentation.',
  },
  {
    code: '-GX',
    name: 'Notice of liability issued voluntarily',
    category: 'payment',
    categoryLabel: 'HCPCS / Payment',
    rule: 'Indicates that a notice of liability was issued voluntarily when required conditions are met.',
    tip: 'Confirm the payer-specific notice requirements before reporting the modifier.',
  },
  {
    code: '-QW',
    name: 'CLIA-waived test',
    category: 'payment',
    categoryLabel: 'HCPCS / Payment',
    rule: 'Identifies a laboratory service performed using a CLIA-waived test.',
    tip: 'The facility must have the appropriate CLIA certificate and the test must qualify as waived.',
  },
];

export const wizardQuestions=[
  {
    id: 'service',
    title: 'What type of service was performed?',
    options: [
      {value: 'em',label: 'Office Visit / E/M',icon: 'Stethoscope'},
      {value: 'surgery',label: 'Surgery / Procedure',icon: 'Scissors'},
      {value: 'diagnostic',label: 'Radiology / Lab',icon: 'Activity'},
      {value: 'supply',label: 'Supply / DME',icon: 'Package'},
    ],
  },
  {
    id: 'scenario',
    title: 'What best describes the clinical scenario?',
    options: [
      {value: 'separate',label: 'Separate service on the same day'},
      {value: 'bilateral',label: 'Bilateral body part'},
      {value: 'unrelated-em',label: 'Unrelated E/M during global period'},
      {value: 'staged',label: 'Staged or planned procedure'},
      {value: 'return',label: 'Unplanned return for a related procedure'},
      {value: 'unrelated-procedure',label: 'Unrelated procedure during global period'},
      {value: 'professional',label: 'Professional interpretation only'},
      {value: 'technical',label: 'Equipment and technical services only'},
      {value: 'separate-encounter',label: 'Separate encounter on the same day'},
      {value: 'separate-practitioner',label: 'Different practitioner performed the service'},
      {value: 'separate-structure',label: 'Separate organ or anatomical structure'},
      {value: 'non-overlapping',label: 'Unusual non-overlapping service'},
      {value: 'increased',label: 'Significantly increased effort or complexity'},
      {value: 'reduced',label: 'Procedure partially reduced at physician discretion'},
      {value: 'discontinued',label: 'Procedure discontinued for patient safety'},
      {value: 'repeat',label: 'Repeat procedure on the same day'},
      {value: 'drug-waste',label: 'Drug discarded from a single-dose vial'},
      {value: 'drug-no-waste',label: 'No drug wasted from a single-dose vial'},
      {value: 'payment',label: 'Payer notice, waiver, or test requirement'},
    ],
  },
  {
    id: 'provider',
    title: 'Was the service performed by the same physician?',
    options: [
      {value: 'same',label: 'Yes, the same physician or qualified provider'},
      {value: 'different',label: 'No, a different provider performed the service'},
    ],
  },
];

const recommendationMap={
  'em:separate:same': '-25',
  'em:unrelated-em:same': '-24',
  'em:staged:same': '-57',
  'surgery:bilateral:same': '-50',
  'surgery:separate:same': '-59',
  'surgery:staged:same': '-58',
  'surgery:return:same': '-78',
  'surgery:unrelated-procedure:same': '-79',
  'surgery:increased:same': '-22',
  'surgery:reduced:same': '-52',
  'surgery:discontinued:same': '-53',
  'surgery:repeat:same': '-76',
  'diagnostic:professional:same': '-26',
  'diagnostic:technical:same': '-TC',
  'diagnostic:separate-encounter:same': '-XE',
  'diagnostic:separate-practitioner:different': '-XP',
  'diagnostic:separate-structure:same': '-XS',
  'diagnostic:non-overlapping:same': '-XU',
  'supply:drug-waste:same': '-JW',
  'supply:drug-no-waste:same': '-JZ',
  'supply:payment:same': '-GA',
  'diagnostic:payment:same': '-QW',
};

export function getWizardRecommendation(answers) {
  const exactKey=`${answers.service}:${answers.scenario}:${answers.provider}`;
  const code=recommendationMap[exactKey];

  if (code) {
    return modifiers.find((modifier)=> modifier.code===code);
  }

  if (answers.scenario==='bilateral') {
    return modifiers.find((modifier)=> modifier.code==='-50');
  }

  if (answers.scenario==='payment') {
    return modifiers.find((modifier)=> modifier.code==='-GA');
  }

  return modifiers.find((modifier)=> modifier.code==='-59');
}